/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _Array$isArray from "@babel/runtime-corejs3/core-js-stable/array/is-array";
import _Object$create from "@babel/runtime-corejs3/core-js-stable/object/create";

/** @module core/modules/object-utils/deep-clone */
import { Types } from '@codistica/types';
import { log } from '../../log.js';
import { forEachSync } from './for-each-sync.js';
import { hasKeys } from './has-keys.js';
import { isPureObject } from './is-pure-object.js';
import { seek } from './seek.js';
var deepCloneSchema = new Types({
  obj: {
    type: '!undefined'
  },
  options: {
    type: 'Object',
    def: {
      copyPrototype: {
        type: 'boolean',
        def: false
      },
      maxDepth: {
        type: 'number',
        def: Infinity
      }
    }
  }
});
/**
 * @typedef deepCloneOptionsType
 * @property {boolean} [copyPrototype=false] - Copy input object prototype to cloned object.
 * @property {number} [maxDepth=Infinity] - Recursion maximum depth.
 */

/**
 * @description Clones the input object/array own elements one-by-one at every depth.
 * @param {(Object<string,*>|Array<*>)} obj - Object<string,*>/array to be cloned.
 * @param {deepCloneOptionsType} [options] - Cloning options.
 * @returns {(Object<string,*>|null)} Cloned object/array.
 */

function deepClone(obj, options) {
  var _this = this,
      _context;

  // TODO: ADD OPTION TO SKIP FUNCTIONS AND OTHER NON CLONABLE ELEMENTS. OR CLONE WITH OTHER METHODS? (STRING/EVAL?). NOT BY DEFAULT.
  var _deepCloneSchema$vali = deepCloneSchema.validate({
    obj: obj,
    options: options
  });

  obj = _deepCloneSchema$vali.obj;
  options = _deepCloneSchema$vali.options;

  if (!deepCloneSchema.isValid()) {
    log.error('deepClone()', 'ARGUMENTS ERROR. ABORTING')();
    return null;
  }

  var objClone = isPureObject(obj) // TODO: CHECK/FIX. COPY PROTOTYPE WHEN REQUESTED AND INPUT HAS PROTOTYPE (CHECK PROTOTYPE EXISTENCE).
  ? options.copyPrototype ? _Object$create(obj) : {} : _Array$isArray(obj) ? [] : null;

  if (options.maxDepth !== Infinity) {
    log.debug('deepClone()', 'SETTING maxDepth WHILE CLONING WILL COPY A REFERENCE OF ELEMENTS BEYOND THAT POINT')();
  }

  forEachSync(obj, _bindInstanceProperty(_context = function _context(val, API) {
    _newArrowCheck(this, _this);

    if (!hasKeys(val) || options.maxDepth === API.depth) {
      seek(objClone, API.path, val);
    }
  }).call(_context, this), {
    maxDepth: options.maxDepth,
    referenceCheck: true,
    onReference: function onReference(val, refPath, API) {
      seek(objClone, API.path, seek(objClone, refPath));
    }
  });
  return objClone;
}

export { deepClone };